import {gql, useQuery} from '@apollo/client';
import {
  BreakpointsEnum,
  StandardContainer,
  Toast,
  ToastType,
  isBreakpointDown,
  useBreakpoint,
  useIsDesktop,
  useIsWeb,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {
  CommonActions,
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {useDispatch, useSelector} from 'Provider';
import {LOADER_ANIMATION} from 'assets/LottieAnimations';
import {Box, HStack, Pressable, View} from 'native-base';
import React, {useCallback, useEffect} from 'react';
import {Platform} from 'react-native';
import DomMarketBanner from 'components/DomMarketBanner';
import LottieView from 'components/LottieView';
import OffersEmptyDisplay from 'components/Offers/EmptyDisplay/OffersEmptyDisplay';
import OffersErrorDisplay from 'components/Offers/ErrorDisplay/OffersErrorDisplay';
import GridListViewToggle from 'components/Offers/Filters/GridListViewToggle';
import OfferDetailsFlyout from 'components/Offers/OfferDetailsFlyout';
import BlurredOverlay from 'components/Offers/OfferDetailsFlyout/BlurredOverlay';
import PageTemplate from 'components/PageTemplate';
import PageTitle from 'components/PageTitle';
import ConnectedFilterBar from 'containers/ConnectedFilterBar';
import ConnectedFilterDisplay from 'containers/ConnectedFilterDisplay';
import ConnectedOfferDetails from 'containers/OfferDetails';
import ConnectedOfferFilters from 'containers/OfferFilters';
import ConnectedOfferGroup from 'containers/OfferGroup';
import ConnectedRefreshBanner from 'containers/RefreshBanner';
import {useGetOffers} from 'hooks/useGetOffers';
import {usePageViewEvent} from 'hooks/usePageViewEvent';
import {isWebViewSelector} from 'store/content';
import {OFFER_TAB, OffersActions} from 'store/offers/offers.reducer';
import {
  hasNewOffersSelector,
  isDatesSelectedSelector,
  isDomMarketBannerVisibleSelector,
  isFavoriteTabSelector,
  offersFailedSelector,
  offersFilteredSelector,
  offersLastQueryTimeSelector,
  offersLoadingSelector,
  showOffersRefreshErrorSelector,
  rawOffersCountSelector,
  selectIsListView,
  selectedDestinationSelector,
  selectedOfferSelector,
  selectedOfferTabSelector,
} from 'store/offers/offers.selectors';
import {ReservationsActions} from 'store/reservations/reservations.reducer';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import ConnectedOffersOverlay from 'containers/ConnectedOffersOverlay';
import {useGetReservations} from 'hooks/useGetReservations';

const GET_OFFERS_PAGE_TITLE = gql`
  query GetOffersPageTitle {
    empire {
      myRewardsKeyValueList {
        offersPageTitle
      }
    }
  }
`;

type ParamsList = {
  OffersGrouping: {group: string; offerid?: string};
};

const OffersScreen: React.FunctionComponent = () => {
  /**
   * Get Offers and Reservations
   */
  useGetOffers();
  useGetReservations();
  /**
   *
   */
  const {
    error: titleError,
    data,
    loading: titleLoading,
  } = useQuery(GET_OFFERS_PAGE_TITLE);
  const pageTitle =
    titleError || titleLoading
      ? 'OFFERS'
      : data?.empire?.myRewardsKeyValueList?.[0].offersPageTitle || '';
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();
  const {trackPageViewEvent} = usePageViewEvent();
  const navigation = useNavigation();
  const route = useRoute<RouteProp<ParamsList, 'OffersGrouping'>>();
  const isOffersLoading = useSelector(offersLoadingSelector);
  const selectedOffer = useSelector(selectedOfferSelector);
  const offersError = useSelector(offersFailedSelector);
  const {results: offers} = useSelector(offersFilteredSelector);
  const offersLastQueryTime = useSelector(offersLastQueryTimeSelector);
  const isWebView = useSelector(isWebViewSelector);
  const rawOffersCount = useSelector(rawOffersCountSelector);
  const isFavoriteTab = useSelector(isFavoriteTabSelector);
  const isDatesSelected = useSelector(isDatesSelectedSelector);
  const isListView = useSelector(selectIsListView);

  const isDesktop = useIsDesktop();
  const isNano = isBreakpointDown(breakpoint, BreakpointsEnum.NANO);
  const isWeb = useIsWeb();
  const {trackAdobeEvent} = useTrackEvent();
  const isFirstLoad = !offersLastQueryTime?.ocrm && !offersLastQueryTime?.all;
  const showingFilteredOrMore =
    !isDesktop && (isDatesSelected || route.params?.group);
  const isLandingScreen = !isDatesSelected && !route.params?.group;
  const isInitialDomMarket = useSelector(isDomMarketBannerVisibleSelector);
  const selectedDestination = useSelector(selectedDestinationSelector);
  const selectedOfferTab = useSelector(selectedOfferTabSelector);
  const isDomMarketBannerVisible = useSelector(
    isDomMarketBannerVisibleSelector
  );
  const hasNewOffers = useSelector(hasNewOffersSelector);
  const showOffersRefreshError = useSelector(showOffersRefreshErrorSelector);
  const shouldShowDomMarketBanner =
    isDomMarketBannerVisible && !isFavoriteTab && !!selectedDestination;

  // Show offer bar:
  // if it's not desktop and
  //   we have any offers
  //   or if we have new offers
  //   or if we have no offers as long as there wasn't an error retrieving offers
  //    (otherwise we show the big error message w/ no filter bar)
  // Currently we don't show the big error message as long as there are cached offers in redux.
  const hasFilterBar =
    !isDesktop &&
    (rawOffersCount > 0 ||
      hasNewOffers ||
      (rawOffersCount === 0 && !offersError));

  const handleCloseOfferDetails = useCallback(
    (clearParam = true) => {
      dispatch(OffersActions.setSelectedOfferId(undefined));
      dispatch(OffersActions.clearAcceptOCRMOfferState());
      // On offer deselect/close
      const params = route.params;
      if (clearParam && params?.offerid) {
        navigation.dispatch(
          CommonActions.setParams({...params, offerid: undefined})
        );
      }
    },
    [dispatch, navigation, route.params]
  );

  const clearFilters = useCallback(
    () => dispatch(OffersActions.clearFilters()),
    [dispatch]
  );

  const renderOffers = () => {
    if (isOffersLoading && isFirstLoad) {
      return (
        <LottieView
          source={LOADER_ANIMATION}
          loop
          autoPlay
          style={{
            width: isWeb ? 550 : '100%',
            height: isWeb ? 550 : undefined,
          }}
        />
      );
    }

    if (!offers.length && !hasNewOffers) {
      return (
        <View
          flex={1}
          marginTop={!isDesktop && !hasFilterBar ? '32px' : undefined}>
          <OffersEmptyDisplay
            isFavoriteTab={isFavoriteTab}
            isInitialDomMarket={isInitialDomMarket}
            onClearPress={clearFilters}
            domMarketName={selectedDestination?.name}
          />
        </View>
      );
    }

    return (
      // 390 instead of 342 to account for filter's 48 right margin
      <Box w={!isDesktop ? '100%' : 'calc(100% - 390px)'}>
        {isLandingScreen && isDesktop && (
          <View
            mb={shouldShowDomMarketBanner || hasNewOffers ? '8px' : undefined}>
            {shouldShowDomMarketBanner && (
              <DomMarketBanner
                destination={selectedDestination?.name.toUpperCase()}
              />
            )}
            <ConnectedRefreshBanner />
          </View>
        )}
        {showOffersRefreshError && (
          <Toast
            toast={{
              type: ToastType.DANGER,
              messageContent:
                "Sorry for the delay - It's taking a bit longer to retrieve your newest offers. Please check back later.",
            }}
            autoHide={false}
          />
        )}
        <ConnectedOfferGroup />
      </Box>
    );
  };

  useEffect(() => {
    /**
     * Should the offer be removed from the list,
     * deselect the currently selected offer, but do not clear the queryParam.
     */
    if (!selectedOffer) {
      handleCloseOfferDetails(false);
    }

    /**
     * TODO: Else, add/set query params for the selected offer?
     * Separate ticket/Alternative methods?
     */
  }, [handleCloseOfferDetails, selectedOffer]);

  useFocusEffect(trackPageViewEvent);

  const headerElements = [
    <PageTitle
      activeTab={selectedOfferTab}
      title={pageTitle}
      key="offers-title"
      navItems={[{text: OFFER_TAB.AVAILABLE}, {text: OFFER_TAB.FAVORITES}]}
      updateTab={(tabName: string) => {
        if (tabName === OFFER_TAB.FAVORITES) {
          trackAdobeEvent({
            event: AdobeEvent.click,
            click: {
              name: 'rewards offers favorites',
            },
          });
        }
        dispatch(OffersActions.setActiveOfferTab(tabName));
      }}
      suppressHeaderFooter={isWebView}
    />,
    hasFilterBar ? (
      <View
        paddingTop="16px"
        backgroundColor="#f2f2f2"
        key="offers-bar"
        zIndex={999}>
        <StandardContainer>
          {isLandingScreen && <ConnectedRefreshBanner />}
          <ConnectedFilterBar />
          <ConnectedFilterDisplay />
          {!isNano && (
            <Box ml={2} mt={2}>
              <GridListViewToggle
                isListView={isListView}
                setListView={() => dispatch(OffersActions.switchIsListView())}
              />
            </Box>
          )}
          {isLandingScreen && shouldShowDomMarketBanner && (
            <View mt="20px">
              <DomMarketBanner
                destination={selectedDestination?.name.toUpperCase()}
              />
            </View>
          )}
          <Box height="16px" opacity="0.20" />
        </StandardContainer>
      </View>
    ) : undefined,
  ];

  const pageContents = (
    <PageTemplate
      stickyHeaderIndices={hasFilterBar ? [1] : undefined} // if filter bar exists, make it sticky
      headerElements={headerElements}>
      <StandardContainer>
        <View pt={isDesktop ? '48px' : null}>
          {rawOffersCount === 0 && offersError ? (
            <View
              minHeight={isDesktop ? '662px' : '343px'}
              marginTop={!isDesktop && !hasFilterBar ? '32px' : undefined}>
              <OffersErrorDisplay />
            </View>
          ) : (
            <View alignItems={showingFilteredOrMore ? 'center' : 'flex-start'}>
              <View width="100%">
                <HStack
                  alignItems="flex-start"
                  paddingLeft={showingFilteredOrMore ? 0 : 3}
                  marginLeft={showingFilteredOrMore ? 0 : '-12px'}>
                  {isDesktop && (
                    <Box w={'342px'} mr="48px" bg="white">
                      <ConnectedOfferFilters />
                    </Box>
                  )}
                  {renderOffers()}
                </HStack>
              </View>
            </View>
          )}
        </View>
      </StandardContainer>
    </PageTemplate>
  );

  return <ConnectedOffersOverlay>{pageContents}</ConnectedOffersOverlay>;
};

export default OffersScreen;
